// Cliente REST hacia nuestra API (backend Fastify). Independiente: no usa
// Firebase ni nada de svabogadas. El token JWT se guarda en localStorage.
export const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');

// El estudio (tenant) se deduce del SUBDOMINIO de la URL. Un solo despliegue
// sirve a todos los estudios: souzavilla.tuapp.cl → "souzavilla".
// En local, los navegadores resuelven *.localhost a 127.0.0.1 sin tocar hosts:
// souzavilla.localhost:3000 → "souzavilla".
// REACT_APP_TENANT queda como respaldo cuando se entra sin subdominio.
function resolveTenant(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const fallback = process.env.REACT_APP_TENANT || '';
  const labels = host.split('.');
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return labels.length > 1 ? labels[0] : fallback; // xxx.localhost → xxx
  }
  // Producción: sub.dominio.tld → primer label (ignorando www).
  if (labels.length > 2 && labels[0] !== 'www') return labels[0];
  return fallback;
}

export const TENANT = resolveTenant();

// ¿Estamos en el dominio raíz (landing pública) y no en el subdominio de un
// estudio? tabularis.cl / www.tabularis.cl / localhost → landing.
// souzavilla.tabularis.cl / souzavilla.localhost → panel del estudio.
export function esApex(): boolean {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (!host || host === 'localhost') return true;
  if (host.endsWith('.localhost')) return host.split('.')[0] === 'www';
  const labels = host.split('.');
  if (labels.length <= 2) return true; // tabularis.cl
  return labels[0] === 'www'; // www.tabularis.cl (apex); sub.tabularis.cl (panel)
}

// URL del panel de un estudio, para redirigir desde la landing.
// En apex, window.location.host ES el dominio base (tabularis.cl / localhost:3000).
export function urlEstudio(subdominio: string): string {
  const { protocol, host } = window.location;
  return `${protocol}//${subdominio}.${host}`;
}

const TOKEN_KEY = 'gestion_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string | null) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

export class ApiError extends Error {
  status: number;
  /** Cuerpo completo de la respuesta de error. El bloqueo por vigencia trae
   *  aquí el motivo y los datos de transferencia para renovar. */
  datos?: any;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const MSJ_SIN_CONEXION = 'No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.';

type Options = {
  method?: string;
  body?: unknown;
  auth?: boolean;
  token?: string;
  /** Corta la petición pasado este tiempo. fetch no trae timeout propio, así
   *  que sin esto una llamada que el servidor nunca contesta deja la interfaz
   *  esperando para siempre. Lo usa el asistente legal, cuyas respuestas
   *  pueden tardar minutos y necesitan un margen mucho mayor al normal. */
  timeoutMs?: number;
};

export async function api<T = any>(path: string, opts: Options = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = opts;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const token = opts.token || getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const control = opts.timeoutMs ? new AbortController() : null;
  const reloj = control ? setTimeout(() => control.abort(), opts.timeoutMs) : null;
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(control ? { signal: control.signal } : {}),
    });
  } catch (e: any) {
    if (e?.name === 'AbortError') throw new ApiError(0, 'La petición tardó demasiado y se canceló.');
    // Sin red o conexión cortada. Cada navegador lo dice a su manera y en
    // inglés —Safari "Load failed", Chrome "Failed to fetch"— y eso llegaba
    // tal cual a la pantalla.
    throw new ApiError(0, MSJ_SIN_CONEXION);
  } finally {
    if (reloj) clearTimeout(reloj);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: any;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    throw new ApiError(res.status, 'El servidor respondió algo inesperado. Intenta de nuevo en un momento.');
  }

  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `Error ${res.status}`;
    // Sesión muerta: el token duró su semana y caducó, o la cuenta se
    // desvinculó del estudio. El panel no se enteraba: seguía abierto con lo
    // último que alcanzó a cargar mientras TODAS las peticiones fallaban, así
    // que la persona trabajaba media hora y recién lo descubría al apretar
    // Guardar — con un "No autenticado" que no dice qué hacer. Se borra el
    // token acá y se avisa para volver solo a la pantalla de acceso.
    //
    // No aplica al login (auth:false, donde el 401 es "clave incorrecta") ni a
    // la consola dueño, que trae su propio token por `opts.token`.
    if (res.status === 401 && auth && !opts.token && typeof window !== 'undefined') {
      setToken(null);
      window.dispatchEvent(new CustomEvent('tabularis:sesion', { detail: data || {} }));
    }
    // Se adjunta el cuerpo completo: el bloqueo por vigencia trae el motivo y
    // los datos de transferencia, y el panel los necesita para la pantalla.
    const err = new ApiError(res.status, msg);
    err.datos = data;
    // Bloqueo por vigencia (pendiente, vencido, suspendido, solo lectura): se
    // avisa por evento global para que el panel muestre la pantalla correcta
    // sin tener que envolver cada una de las llamadas que ya existen.
    if ((res.status === 402 || res.status === 403) && data?.motivo && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tabularis:bloqueo', { detail: data }));
    }
    throw err;
  }
  return data as T;
}
